import { ALL_FORMATS, BlobSource, Input } from "mediabunny";

export interface AudioInspection {
  /** Whether the file has a (primary) audio track at all. */
  hasAudio: boolean;
  /** The audio codec (e.g. `"aac"`, `"ac3"`), or null if unknown/none. */
  codec: string | null;
  /**
   * Whether this browser's WebCodecs can decode that audio. When false with
   * `hasAudio` true, any processed output will be silent (the audio can't be
   * read) — e.g. AC-3/DTS BluRay audio in Chromium.
   */
  decodable: boolean;
}

/**
 * Inspect a media file's primary audio track and whether the browser can decode
 * it. Cheap (reads container metadata only, no decoding) and browser-only. Used
 * to warn before a processing step silently drops undecodable audio.
 */
export async function inspectAudio(file: Blob): Promise<AudioInspection> {
  const input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
  try {
    const track = await input.getPrimaryAudioTrack();
    if (!track) return { hasAudio: false, codec: null, decodable: false };
    return { hasAudio: true, codec: track.codec, decodable: await track.canDecode() };
  } catch {
    return { hasAudio: false, codec: null, decodable: false };
  } finally {
    input.dispose();
  }
}
